import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ChecklistTypeFindInterface } from '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { ChecklistTemplateFormInterface } from '@zaoblako/shared/modules/project/dto/checklist-template/checklist-template-form.dto';
import type { ProjectTemplateFindInterface } from '@zaoblako/shared/modules/project/dto/template/project-template-find.dto';
import {
  ProjectTemplateFormDto,
  type ProjectTemplateFormInterface
} from '@zaoblako/shared/modules/project/dto/template/project-template-form.dto';
import { SubsKindEnum } from '@zaoblako/shared/modules/project/enum/subs.kind.enum';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { ProjectChecklistService } from '@zaoblako/web/core/services/project/project-checklist.service';
import { ProjectMasterMilestoneService } from '@zaoblako/web/core/services/project/project-master-milestone.service';
import { ProjectTemplateService } from '@zaoblako/web/core/services/project/project-template.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { CreatedProjectsWidgetComponent } from '@zaoblako/web/modules/project/dumbs/project-template-form/created-projects-widget/created-projects-widget.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { AutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/auto-complete/auto-complete.component';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DealMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/deal-multi-select/deal-multi-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PrioritySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/priority-single-select/priority-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { PlanningDeadlineFormComponent } from '@zaoblako/web/shared/dumbs/planning-deadline-form/planning-deadline-form.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TemplateSubjectEditorComponent } from '@zaoblako/web/shared/dumbs/template-subject-editor/template-subject-editor.component';
import { TemplateSubjectInputComponent } from '@zaoblako/web/shared/dumbs/template-subject-input/template-subject-input.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-project-template-form',
  templateUrl: './project-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    TemplateSubjectInputComponent,
    JoinPipe,
    CheckboxModule,
    FormsModule,
    SingleSelectComponent,
    MultiSelectComponent,
    AutoCompleteComponent,
    ButtonModule,
    TemplateSubjectEditorComponent,
    DividerModule,
    PlanningDeadlineFormComponent,
    PlanningPeriodicityCardComponent,
    UserMultiSelectComponent,
    DealMultiSelectComponent,
    DirectionSingleSelectComponent,
    CreatedProjectsWidgetComponent,
    PrioritySingleSelectComponent,
    SpinnerComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    MasterMilestoneSingleSelectComponent,
    UserSingleSelectComponent,
    ClientAutoCompleteComponent,
    DatepickerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectTemplateFormComponent extends BaseFormComponent<ProjectTemplateFormInterface> implements OnInit {
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public readonly index: number;
  public template = <ProjectTemplateFormInterface>{};
  public dto = ProjectTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public projects: ProjectFindInterface[] = [];
  public checklistTypes: ChecklistTypeFindInterface[] = [];
  public masterMilestones: MasterMilestoneFindInterface[] = [];
  public templates: ProjectTemplateFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public createdProjects: ProjectFindInterface[] = [];
  public createdProjectsLoading: boolean;
  public periodicity: TemplateEntityPeriodicityEnum;
  public tasks: HelpdeskTaskFindInterface[] = [];

  public participants: UserFindInterface[] = [];

  public memberInfo: {
    creator: UserFindInterface;
    responsible: UserFindInterface;
    auditorUsers: UserFindInterface[];
  } = {
    creator: null,
    responsible: null,
    auditorUsers: []
  };

  public today = moment().format('YYYY-MM-DD');

  public get TemplateEntityPeriodicityEnum() {
    return TemplateEntityPeriodicityEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly authService: AuthService,
    private readonly projectTemplateService: ProjectTemplateService,
    private readonly projectService: ProjectService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly projectChecklistService: ProjectChecklistService,
    private readonly projectMasterMilestoneService: ProjectMasterMilestoneService,
    private readonly directionService: DirectionService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public ngOnInit() {
    const projectId = this.formStoreService.getState.forms[this.index]?.data?.projectId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(projectId)) {
      forkJoin([
        this.projectService.findById<ProjectFindInterface>(projectId),
        this.projectChecklistService.getChecklistsByProjectId(projectId),
        this.projectService.getDeals(projectId, { statusKind: SubsKindEnum.ALL })
      ]).subscribe(([project, checklists, deals]) => {
        const _checklists = checklists.map((checklist) => {
          return <ChecklistTemplateFormInterface>{
            name: checklist.name,
            type: checklist.type,
            masterMilestone: checklist.masterMilestone,
            responsible: checklist.responsible,
            description: checklist.description,
            deadline: {
              daysAfter: 0,
              type: DeadlineTypeEnum.RELATIVE
            }
          };
        });

        this.template = {
          disabled: false,
          name: project.name,
          responsible: project.responsible,
          masterMilestone: project.masterMilestone,
          parentProject: project.parent,
          description: project.description,
          userAuditors: project.userAuditors,
          userGroupAuditors: project.userGroupAuditors,
          direction: project.direction,
          unit: project.unit,
          client: project.client,
          checklists: _checklists,
          planning: {
            periodicity: TemplateEntityPeriodicityEnum.NONE
          },
          deadline: {
            daysAfter: 0,
            type: DeadlineTypeEnum.RELATIVE
          },
          filial: project.filial,
          deals: deals,
          priority: project.priority
        };
        this.cdRef.markForCheck();
      });
    }

    forkJoin([
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.projectChecklistService.getChecklistTypes(),
      this.projectMasterMilestoneService.find<MasterMilestoneFindInterface>(),
      this.projectTemplateService.findAll()
    ]).subscribe(([users, userGroups, checklistTypes, masterMilestones, templates]) => {
      this.users = users;
      this.userGroups = userGroups;
      this.checklistTypes = checklistTypes;
      this.masterMilestones = masterMilestones;
      this.templates = templates;
      this.cdRef.markForCheck();
    });

    forkJoin([this.directionService.find<DirectionFindInterface>()]).subscribe(([directions]) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.template.planning = <PlanningUnitFormInterface>{};
      this.template.planning.periodicity = TemplateEntityPeriodicityEnum.NONE;

      this.template.deadline = <DeadlineFormInterface>{};
      this.template.deadline.daysAfter = 0;
      this.template.deadline.type = DeadlineTypeEnum.RELATIVE;
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.projectTemplateService
      .findById<ProjectTemplateFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .pipe(
        tap((template) => {
          this.template = <any>template;
          this.periodicity = template.planning.periodicity;
          this.memberInfo.creator = template.creator as UserFindInterface;
          this.updateMembers();
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }),
        switchMap((template: ProjectTemplateFindInterface) => {
          if (template.planning?.periodicity !== TemplateEntityPeriodicityEnum.NONE) {
            return this.projectTemplateService.getCreatedProjectsWidget(
              this.formStoreService.getState.forms[this.index]?.entityId
            );
          }
          return of([]);
        })
      )
      .subscribe({
        next: (projects) => {
          this.createdProjects = projects;
          this.createdProjectsLoading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.createdProjectsLoading = false;
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTemplate() {
    if (this.checkValidate(this.template).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.projectTemplateService
          .update<ProjectTemplateFormInterface, ProjectTemplateFindInterface>(this.template)
          .subscribe({
            next: (template) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон "${template.name}" обновлен`);
              this.formStoreService.saveNext(template, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.projectTemplateService.create<ProjectTemplateFindInterface>(this.template).subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон создан');
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон "${this.template.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.projectTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон "${this.template.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public projectSearch(q: string) {
    this.projectService.getAccessibleProjects({ search: q }).subscribe((projects) => {
      this.projects = projects;
      this.cdRef.markForCheck();
    });
  }

  public updateMembers() {
    this.memberInfo = {
      creator: this.memberInfo.creator,
      responsible: this.template.responsible as UserFindInterface,
      auditorUsers: []
    };

    if (this.template.userGroupAuditors?.length || this.template.userAuditors?.length) {
      const auditorUsers = [...this.template.userAuditors];
      this.template.userGroupAuditors.forEach((auditor) => {
        if (auditor.users?.length) {
          auditorUsers.push(...auditor.users);
        }
      });

      this.memberInfo.auditorUsers = auditorUsers as UserFindInterface[];
    }

    const members = [];
    members.push(this.memberInfo?.creator ? this.memberInfo.creator : this.authService.currentUser);
    if (this.memberInfo?.responsible) {
      members.push(this.memberInfo.responsible);
    }
    if (this.memberInfo?.auditorUsers?.length) {
      members.push(...this.memberInfo.auditorUsers);
    }
    this.participants = members.reduce((array: UserFindInterface[], user) => {
      if (array.findIndex((item) => item.id === user.id) === -1) {
        array.push(user);
      }
      return array;
    }, []);
  }

  public addChecklist() {
    if (!this.template.checklists) {
      this.template.checklists = [];
    }
    const checklist = <ChecklistTemplateFormInterface>{};
    checklist.deadline = <DeadlineFormInterface>{};
    checklist.deadline.type = DeadlineTypeEnum.RELATIVE;
    checklist.deadline.daysAfter = 0;
    this.template.checklists.push(checklist);
  }

  public deleteChecklist(checklist: ChecklistTemplateFormInterface) {
    this.template.checklists = this.template.checklists.filter((_checklist) => _checklist !== checklist);
  }

  public directToProjectList() {
    const queryParams = {
      listStyle: 'flat',
      templateIds: [this.formStoreService.getState.forms[this.index]?.entityId]
    };
    const url = String(this.router.createUrlTree(['/project/list'], { queryParams }));
    window.open(url, '_blank');
  }
}
